"use client";
import React, { useState, useEffect, memo } from "react";
import Image from "next/image";
import { Source_Serif_4 } from "next/font/google";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

// Shape of a ui-landing.logo-list entry as Strapi returns it: the media object
// is itself named `logos`, matching presentedBy on the same landing page.
export interface CountdownLogo {
  id?: number | string;
  companyName?: string;
  logos?: {
    url?: string;
    width?: number;
    height?: number;
  };
}

export interface CountdownBackgroundImage {
  url?: string;
}

interface CountdownTimerProps {
  targetDate: Date | string;
  title?: string;
  timerTextColor: string;
  logos?: CountdownLogo[];
  logosTitle?: string;
  backgroundImage?: CountdownBackgroundImage;
  onComplete?: () => void;
}

const sourceSerif4 = Source_Serif_4({
  weight: "600",
  style: "italic",
  subsets: ["latin"],
});

const TimeUnit = memo(({ value, label, timerTextColor }: { value: number; label: string; timerTextColor: string; }) => (
  <div className="flex flex-col gap-1 sm:gap-2 items-center shrink-0 min-w-[60px] md:min-w-[80px]">
    <time 
      className={"leading-none text-4xl md:text-5xl lg:text-6xl " + sourceSerif4.className}
      style={{ color: timerTextColor }}
      dateTime={`P${value}${label.charAt(0)}`}
    >
      {String(value).padStart(2, "0")}
    </time>
    <span className="font-sans text-[10px] sm:text-xs uppercase tracking-widest text-white/70">
      {label}
    </span>
  </div>
));

TimeUnit.displayName = "TimeUnit";

const Separator = memo(({ color }: { color: string }) => (
  <div 
    className="flex items-center justify-start h-full" 
    aria-hidden="true"
  >
    <span 
      className={`text-3xl md:text-5xl opacity-50 ${sourceSerif4.className}`} 
      style={{ color: color }}
    >
      :
    </span>
  </div>
));

Separator.displayName = "Separator";

const CountdownLogos = memo(
  ({ logos, title }: { logos: CountdownLogo[]; title?: string }) => {
    // next/image throws when src is undefined, and a CMS entry can exist with an
    // empty media field, so entries without a URL are dropped instead of rendered.
    // Intrinsic dimensions come from Strapi so each logo keeps its own aspect
    // ratio at a shared height, rather than being squeezed into equal-width boxes.
    const items = logos.flatMap((logo) => {
      const media = logo?.logos;
      if (!media?.url) return [];
      return [
        {
          key: logo?.id ?? media.url,
          url: media.url,
          alt: logo?.companyName || "Partner",
          width: media.width ?? 200,
          height: media.height ?? 60,
        },
      ];
    });

    if (items.length === 0) return null;

    return (
      <div className="flex flex-col items-center gap-6 md:gap-8 pb-10 md:pb-14">
        {/* An editor who clears the CMS field gets no heading; an environment
            where the field does not exist yet falls back to the default. */}
        {!!title && (
          <p className="font-h4 text-center text-white">
            {title}
          </p>
        )}

        <div className="flex flex-wrap gap-x-10 gap-y-6 md:gap-x-16 md:gap-y-8 items-center justify-center">
          {items.map((item) => (
            <Image
              key={item.key}
              src={item.url}
              alt={item.alt}
              width={item.width}
              height={item.height}
              className="h-12 w-auto md:h-countdown-logo"
              // remotePatterns already allow the S3 host. The rest of this
              // site skips /_next/image for Strapi media; do the same here.
              unoptimized
            />
          ))}
        </div>
      </div>
    );
  },
);

CountdownLogos.displayName = "CountdownLogos";

export default function CountdownTimer({
  targetDate,
  title = "Event starts in",
  timerTextColor="#FFFFFF",
  logos,
  logosTitle = "Featuring speakers from",
  backgroundImage,
  onComplete,
}: CountdownTimerProps) {
  const [mounted, setMounted] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const calculateTimeLeft = (): TimeLeft | null => {
      const target = typeof targetDate === "string" ? new Date(targetDate) : targetDate;
      const difference = target.getTime() - Date.now();

      if (difference <= 0) {
        setIsExpired(true);
        onComplete?.();
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    // Initial check
    const initialTime = calculateTimeLeft();
    if (initialTime) setTimeLeft(initialTime);

    const timer = setInterval(() => {
      const newTime = calculateTimeLeft();
      if (newTime) {
        setTimeLeft(newTime);
      } else {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, onComplete, mounted]);

  // Client-only timer digits would mismatch SSR. Reserve the same
  // minimum height the section uses so the page does not jump as much.
  if (!mounted) return <div className="min-h-[200px] w-full" />;

  const timer = isExpired ? (
    <div className="flex flex-col items-center gap-2 animate-in fade-in zoom-in duration-500">
      <h2 className={"text-4xl md:text-5xl lg:text-6xl " + sourceSerif4.className} style={{ color: timerTextColor }}>
        Session completed
      </h2>
      <p className="font-sans text-sm uppercase tracking-[0.2em] text-white/50">
        Thank you for joining us
      </p>
    </div>
  ) : (
    <>
      <h2 className="text-center text-sm font-medium uppercase tracking-[0.2em] text-white opacity-80">
        {title}
      </h2>
      <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-6 lg:gap-10">
        <TimeUnit value={timeLeft.days} label="Days" timerTextColor={timerTextColor} />
        <Separator color={timerTextColor} />
        <TimeUnit value={timeLeft.hours} label="Hours" timerTextColor={timerTextColor} />
        <Separator color={timerTextColor} />
        <TimeUnit value={timeLeft.minutes} label="Min" timerTextColor={timerTextColor} />
        <Separator color={timerTextColor} />
        <TimeUnit value={timeLeft.seconds} label="Sec" timerTextColor={timerTextColor} />
      </div>
    </>
  );

  return (
    <section
      className="relative w-full overflow-hidden bg-[#0b0f0c] py-12 md:py-20"
      aria-live="polite"
    >
      {backgroundImage?.url && (
        <Image
          src={backgroundImage.url}
          alt=""
          aria-hidden="true"
          fill
          sizes="100vw"
          className="object-cover opacity-30 pointer-events-none select-none"
          // Same as the logos: Strapi media is not run through /_next/image.
          unoptimized
        />
      )}
      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center px-4">
        {logos?.length ? <CountdownLogos logos={logos} title={logosTitle} /> : null}
        <div className="flex w-fit max-w-full flex-col items-center gap-4 rounded-lg bg-black/60 px-4 py-6 sm:px-10 md:px-14 lg:px-16">
          {timer}
        </div>
      </div>
    </section>
  );
}